import { VIEWPORT_WIDTHS } from "./constants";

export interface ImageVariants {
  large: string;
  medium: string;
  small: string;
  thumbnail: string;
}

export interface NavigatorWithConnection extends Navigator {
  connection?: {
    effectiveType: string;
  };
}

const NETWORK_EFFECTIVE_TYPE = {
  SLOW_2G: "slow-2g",
  TWO_G: "2g",
  THREE_G: "3g",
} as const;

const isNavigatorWithConnection = (
  navigator: Navigator,
): navigator is NavigatorWithConnection =>
  "connection" in navigator && navigator.connection !== undefined;

export const isFastNetwork = (): boolean => {
  const connection = isNavigatorWithConnection(navigator)
    ? navigator.connection
    : undefined;

  // Unknown (e.g. no Network Information API support, all of iOS Safari) is
  // not the same as fast: assume slow so the loading placeholder shows
  // instead of a blank flash.
  if (!connection) return false;

  const effectiveType = connection.effectiveType;
  const isSlow3gNetwork =
    effectiveType === NETWORK_EFFECTIVE_TYPE.TWO_G ||
    effectiveType === NETWORK_EFFECTIVE_TYPE.SLOW_2G;
  const isThreeGNetwork = effectiveType === NETWORK_EFFECTIVE_TYPE.THREE_G;

  return !isSlow3gNetwork && !isThreeGNetwork;
};

export const shouldSkipThumbnail = (): boolean =>
  isFastNetwork() || !isNavigatorWithConnection(navigator);

const getSlow3gImageSrc = (
  srcVariants: ImageVariants | undefined,
  fallbackSrc: string,
): string => srcVariants?.thumbnail || fallbackSrc;

const getThreeGImageSrc = (
  width: number,
  srcVariants: ImageVariants | undefined,
  fallbackSrc: string,
): string => {
  if (!srcVariants) return fallbackSrc;

  if (width <= VIEWPORT_WIDTHS.small) {
    return srcVariants.small;
  }

  if (width <= VIEWPORT_WIDTHS.medium) {
    return srcVariants.medium;
  }

  return srcVariants.large;
};

const getFastNetworkImageSrc = (
  width: number,
  srcVariants: ImageVariants | undefined,
  fallbackSrc: string,
): string => {
  if (!srcVariants) return fallbackSrc;

  if (width >= VIEWPORT_WIDTHS.small) {
    return srcVariants.large;
  }

  return fallbackSrc;
};

export const getOptimalImageSrc = (
  width: number,
  srcVariants: ImageVariants | undefined,
  fallbackSrc: string,
): string => {
  if (!srcVariants) return fallbackSrc;

  const connection = isNavigatorWithConnection(navigator)
    ? navigator.connection
    : undefined;

  const effectiveType = connection?.effectiveType;
  const isSlow3gNetwork =
    effectiveType === NETWORK_EFFECTIVE_TYPE.TWO_G ||
    effectiveType === NETWORK_EFFECTIVE_TYPE.SLOW_2G;
  const isThreeGNetwork = effectiveType === NETWORK_EFFECTIVE_TYPE.THREE_G;

  if (isSlow3gNetwork) {
    return getSlow3gImageSrc(srcVariants, fallbackSrc);
  }

  if (isThreeGNetwork) {
    return getThreeGImageSrc(width, srcVariants, fallbackSrc);
  }

  return getFastNetworkImageSrc(width, srcVariants, fallbackSrc);
};
